/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Scripted agent step
 * lists for the sourcing flows (AI event drafting, contract drafting after an
 * award, counter-offers), plus a helper that turns a store cursor into step
 * statuses so progress survives tab switches and navigation.
 */
import type {
  ScriptedStep,
  ScriptedStepStatus,
} from '../../components/useScriptedSteps';
import { getPerson } from '../../data/company';
import { getSupplier } from '../../data/suppliers';
import { BIDS, landedPerUnit } from './sourcing-data';
import { fmtMillions, fmtPerUnit } from './sourcing-format';

const COUNSEL = getPerson('elena-novak');

/** Steps shown while the AI drafts an event from a one-line prompt. */
export const CREATE_EVENT_STEPS: ScriptedStep[] = [
  {
    label: 'Analyzing 24 months of spend',
    detail: `Dry-type transformers: ${fmtMillions(getSupplier('sup-stellar').spendTtm)} a year across the Dallas, Phoenix and Hayward DCs, all from ${getSupplier('sup-stellar').name} under SA-STLR-2024-005`,
    durationMs: 1000,
  },
  {
    label: 'Pulling requirements from agreements and specifications',
    detail:
      'Read SA-STLR-2024-005 and the transformer specifications: UL certification, federal energy-efficiency rules and 180 days of notice before changes',
    durationMs: 1000,
  },
  {
    label: 'Finding suitable suppliers (3 existing + 4 market)',
    detail:
      '7 candidates screened on UL certification, efficiency-rule compliance and North American capacity',
    durationMs: 1100,
  },
  {
    label: 'Drafting the questions for suppliers',
    detail:
      'Scope, requirements, evaluation criteria, timeline and invitation list',
    durationMs: 900,
  },
];

/** Contract drafting after the award. Details are filled in by the page. */
export const CONTRACT_DRAFT_STEP_COUNT = 5;

export const CONTRACT_DRAFT_DURATIONS: number[] = [900, 1000, 1100, 1000, 800];

export function contractDraftSteps(negotiated: boolean): ScriptedStep[] {
  return [
    {
      label: 'Selecting the Pillar supply agreement template',
      detail: 'Direct materials supply agreement, template v7.2',
    },
    {
      label: 'Populating commercial terms from the awarded bids',
      detail: `Aurelia ${fmtPerUnit(landedPerUnit(BIDS['sup-aurelia']))} and Northgate ${fmtPerUnit(landedPerUnit(BIDS['sup-northgate']))} per fixture, 60% and 40% shares, delivered with freight included to the Dallas, Phoenix and Hayward DCs`,
    },
    {
      label: 'Applying the standard clause rules',
      detail: negotiated
        ? '180 days of notice before changes, three-year price hold with capped aluminium price changes, Net 60 (Aurelia) and Net 45 (Northgate)'
        : '180 days of notice before changes, three-year price hold with capped aluminium price changes, Net 60 (Aurelia) and Net 30 flagged (Northgate)',
    },
    {
      label: 'Generating draft agreements',
      detail:
        'Aurelia and Northgate drafts for 2027 to 2029, replacing SA-AURE-2024-009 and SA-NGTE-2024-012',
    },
    {
      label: 'Routing for legal review',
      detail: `Assigned to ${COUNSEL.name}, ${COUNSEL.title}`,
    },
  ];
}

export const COUNTER_STEPS: ScriptedStep[] = [
  {
    label: 'Drafting counter-offers for Northgate and Aurelia',
    detail:
      'Targets and walk-away points from the plan, checked against both current agreements',
  },
  {
    label: 'Checking asks against Pillar policy',
    detail:
      'Net 60 and 180 days of notice before changes are policy minimums, so both are marked as firm',
  },
  {
    label: 'Sending emails to supplier contacts',
    detail: `2 emails sent, ${COUNSEL.name} copied for contract terms`,
  },
  {
    label: 'Waiting for supplier replies',
    detail: 'Replies are monitored and parsed against each ask',
  },
  {
    label: 'Northgate replied',
    detail:
      'Accepts Net 45, drops the account-team fee, agrees to 180 days of notice',
  },
  {
    label: 'Aurelia replied',
    detail:
      'Accepts capped aluminium price changes, forecast-only volume and price-neutral conversion',
  },
  {
    label: 'Updating projected savings',
    detail: 'Recomputed from the negotiated terms',
  },
];

export const COUNTER_DURATIONS: number[] = [
  900, 900, 900, 1300, 1000, 1000, 800,
];

/** Convert a store cursor (-1 idle, n running, count done) into step statuses. */
export function cursorStatuses(
  count: number,
  cursor: number,
): ScriptedStepStatus[] {
  return Array.from({ length: count }, (_, i): ScriptedStepStatus => {
    if (cursor < 0) return 'pending';
    if (i < cursor) return 'done';
    return i === cursor ? 'running' : 'pending';
  });
}
